import { queryOptions } from '@tanstack/react-query'
import { sort } from 'remeda'
import { mainnet } from 'viem/chains'
import { z } from 'zod'

import { SavingsRateChartData } from './types'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { sparkDataApiUrl } from '@/config/consts'
import { percentageAboveOneSchema } from '@/domain/common/validation'
import { Token } from '@/domain/types/Token'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sparkVaultsV2SavingsRateQueryOptions({
  savingsToken,
  chainId,
  historyCutoff,
}: {
  savingsToken: Token
  chainId: number
  historyCutoff?: Date
}) {
  return queryOptions({
    queryKey: sparkVaultsV2SavingsRateQueryKey({ savingsToken, chainId }),
    queryFn: () => sparkVaultsV2SavingsRateQueryFunction({ savingsToken, historyCutoff, chainId }),
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function sparkVaultsV2SavingsRateQueryKey({ savingsToken, chainId }: { savingsToken: Token; chainId: number }) {
  return ['spark-vaults-v2-savings-rate', savingsToken.address, chainId]
}

async function sparkVaultsV2SavingsRateQueryFunction({
  savingsToken,
  historyCutoff,
  chainId,
}: {
  savingsToken: Token
  historyCutoff?: Date
  chainId: number
}): Promise<SavingsRateChartData> {
  const isSandbox = !SUPPORTED_CHAIN_IDS.includes(chainId)
  const res = await fetch(
    `${sparkDataApiUrl}/tokens/${savingsToken.address}/historic/?chainid=${isSandbox ? mainnet.id : chainId}`,
  )
  if (!res.ok) {
    throw new Error(`Failed to fetch savings rate data: ${res.statusText}`)
  }
  const result = await res.json()
  const { data } = sparkVaultsV2SavingsRateDataResponseSchema.parse(result)

  const sortedApy = sort(data, (a, b) => a.date.getTime() - b.date.getTime())

  if (historyCutoff) {
    const filteredApy = sortedApy.filter((item) => item.date > historyCutoff)
    if (filteredApy.length === 1) {
      filteredApy.push({ date: new Date(), rate: filteredApy[0]!.rate })
    }

    return {
      apy: filteredApy,
    }
  }

  return {
    apy: sortedApy,
  }
}

const sparkVaultsV2SavingsRateDataResponseSchema = z.object({
  data: z.array(
    z.object({
      date: z.string().transform((value) => new Date(value)),
      rate: percentageAboveOneSchema,
    }),
  ),
  success: z.literal(true),
})
